import type { Task } from "@app/core";

// Greedy row assignment: returns task.id → row index so same-week all-day
// tasks always occupy the same vertical slot across every cell in the week.
export function computeAllDayRows(tasks: Task[]): Map<string, number> {
  const sorted = [...tasks].sort((a, b) => {
    // Events take priority over tasks for the top rows.
    const ev = Number(b.is_event) - Number(a.is_event);
    if (ev !== 0) return ev;
    if (!a.due_at && !b.due_at) return a.id.localeCompare(b.id);
    if (!a.due_at) return 1;
    if (!b.due_at) return -1;
    const d = a.due_at.localeCompare(b.due_at);
    if (d !== 0) return d;
    const dur = b.duration_minutes - a.duration_minutes;
    return dur !== 0 ? dur : a.id.localeCompare(b.id);
  });
  const rowMap = new Map<string, number>();
  // Per-row list of occupied [start,end] day intervals (inclusive, ms epoch).
  // We track the ACTUAL intervals, not just each row's latest end: the sort is
  // event-priority (events before tasks regardless of date), NOT start-time, so
  // a row's max end isn't monotonic. A single late-ending event would otherwise
  // mark its whole row "busy" and bump an earlier, non-overlapping task down a
  // row, leaving a phantom gap above it.
  const rows: Array<Array<[number, number]>> = [];
  for (const task of sorted) {
    let start: number, end: number;
    if (!task.due_at) { start = -Infinity; end = Infinity; }
    else { start = dayStart(new Date(task.due_at)).getTime(); end = taskEndDay(task).getTime(); }
    let row = 0;
    while (row < rows.length && rows[row]!.some(([rs, re]) => rs <= end && start <= re)) row++;
    (rows[row] ??= []).push([start, end]);
    rowMap.set(task.id, row);
  }
  return rowMap;
}

export type AllDayPosition = "first" | "middle" | "last" | "only";

export function dayStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDaysLocal(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function spanDays(t: Task): number {
  return t.all_day && t.duration_minutes > 0 ? Math.ceil(t.duration_minutes / 1440) : 1;
}

export function taskEndDay(t: Task): Date {
  if (!t.due_at) return new Date();
  return addDaysLocal(dayStart(new Date(t.due_at)), spanDays(t) - 1);
}

export function isMultiDay(t: Task): boolean {
  return t.all_day && t.duration_minutes > 1440;
}

export function getAllDayPosition(task: Task, cellDate: Date): AllDayPosition {
  if (!isMultiDay(task) || !task.due_at) return "only";
  const start = dayStart(new Date(task.due_at));
  const end = taskEndDay(task);
  const cell = dayStart(cellDate);
  const isFirst = cell.getTime() === start.getTime();
  const isLast = cell.getTime() === end.getTime();
  if (isFirst && isLast) return "only";
  if (isFirst) return "first";
  if (isLast) return "last";
  return "middle";
}
